/**
 * NotificationContext
 *
 * Single source of truth for notification state. Eliminates duplicate
 * socket listeners and state races from multiple modal instances.
 *
 * Two modes:
 * - Detail view: auto-opens via socket push_notification event
 * - List view: opens on bell click, auto-marks all as read
 */

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSocket } from '@/hooks/useSocket';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AppNotification {
  id: string;
  type: string;
  title: string | null;
  content: string;
  isRead: boolean;
  deepLinkUrl: string | null;
  createdAt: string;
}

type ViewMode = 'list' | 'detail';

interface NotificationContextValue {
  unreadCount: number;
  notifications: AppNotification[];
  isLoading: boolean;
  isFetching: boolean;
  total: number;
  hasMore: boolean;
  open: boolean;
  viewMode: ViewMode;
  detailNotification: AppNotification | null;
  openListView: () => void;
  closeModal: () => void;
  loadMore: () => void;
  markRead: (ids: string[]) => void;
  switchToList: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

// ─── API helpers ──────────────────────────────────────────────────────────────

async function fetchNotifications(page: number) {
  const limit = page * 20;
  const res = await api.get<{
    success: boolean;
    data: AppNotification[];
    pagination: { total: number; limit: number; offset: number };
  }>(`/notifications?limit=${limit}&offset=0`);
  return res;
}

async function fetchUnreadCount() {
  const res = await api.get<{ success: boolean; data: { count: number } }>('/notifications/unread-count');
  return res.data.count;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [detailNotification, setDetailNotification] = useState<AppNotification | null>(null);
  const queryClient = useQueryClient();
  const { socket, isConnected } = useSocket();
  const { isAuthenticated } = useAuth();

  // Unread count — drives the badge
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notification-unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30_000,
    enabled: isAuthenticated,
  });

  // Notification list — only fetched when list view is open
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['notifications', page],
    queryFn: () => fetchNotifications(page),
    enabled: open && viewMode === 'list',
    placeholderData: (prev) => prev,
  });

  const allNotifications = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const hasMore = allNotifications.length < total;

  // Mark individual as read (detail view)
  const markReadMutation = useMutation({
    mutationFn: (ids: string[]) => api.post('/notifications/mark-read', { ids }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Mark all as read (fires on list view open)
  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/mark-all-read', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.setQueryData(['notification-unread-count'], 0);
    },
  });

  // Socket listener — SOLE trigger for detail view
  useEffect(() => {
    if (!socket || !isConnected) return;
    const onNotification = (payload: AppNotification) => {
      setDetailNotification({
        ...payload,
        isRead: false,
        deepLinkUrl: payload.deepLinkUrl ?? null,
      });
      setViewMode('detail');
      setOpen(true);
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    };
    socket.on('push_notification', onNotification);
    return () => { socket.off('push_notification', onNotification); };
  }, [socket, isConnected, queryClient]);

  // Bell click — open list view, mark all read
  const openListView = useCallback(() => {
    setViewMode('list');
    setPage(1);
    setOpen(true);
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    markAllRead.mutate();
  }, [queryClient, markAllRead]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const loadMore = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const markRead = useCallback((ids: string[]) => {
    markReadMutation.mutate(ids);
  }, [markReadMutation]);

  // Switch from detail to list view
  const switchToList = useCallback(() => {
    setViewMode('list');
    setPage(1);
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    markAllRead.mutate();
  }, [queryClient, markAllRead]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      notifications: allNotifications,
      isLoading,
      isFetching,
      total,
      hasMore,
      open,
      viewMode,
      detailNotification,
      openListView,
      closeModal,
      loadMore,
      markRead,
      switchToList,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
